import type {ReactNode} from 'react'
import {TaskListView} from '../task-list/TaskListView'
import {TaskListItem} from '../task-list/TaskListItem'
import {TaskDescription} from '../task-list/TaskDescription'
import {TaskRelativeTime} from '../task-list/TaskRelativeTime'
import {RepoFilterPicker} from './RepoFilterPicker'
import {useTasks} from '../../hooks/useTasks'
import {getReadableSessionState} from '../../types/session'
import type {UITask} from '../../types/task'
import {useSearchParams} from '@github-ui/react-router'
import {useCurrentUserOptional} from '../../contexts/CurrentUserContext'

const PAGE_SIZE = 10

export interface AgentsTaskListProps {
  activeTab: 'active' | 'completed'
  setActiveTab: (tab: 'active' | 'completed') => void
  repoFilter?: string
  setRepoFilter?: (repo: {nameWithOwner: string} | undefined) => void
  creatorIds?: number[]
  statuses?: string[]
}

function getSessionStateText(task: UITask): string {
  const prState = task.pull?.state ?? 'open'
  const prInMergeQueue = task.pull?.inMergeQueue ?? false

  if (prState === 'merged') return 'Merged'
  if (prState === 'closed') return 'Closed'
  if (prInMergeQueue) return 'Queued to merge'
  return getReadableSessionState(task.latest_session_state)
}

export function AgentsTaskList({
  activeTab,
  setActiveTab,
  repoFilter,
  setRepoFilter,
  creatorIds,
  statuses,
}: AgentsTaskListProps) {
  const [searchParams] = useSearchParams()
  const currentUser = useCurrentUserOptional()
  const currentUserId = currentUser?.currentUser?.id
  const currentPage = Number(searchParams.get('page') ?? '1')

  const {tasks, isLoading, isFetching, isError, hasNextPage, counts, dataUpdatedAt} = useTasks({
    taskFilter: activeTab === 'completed' ? 'completed' : 'active',
    includeCounts: true,
    currentPage,
    pageSize: PAGE_SIZE,
    repoFilter,
    creatorIds,
    statuses,
  })

  const additionalFilters = setRepoFilter ? (
    <RepoFilterPicker repoFilter={repoFilter} onFilterChange={setRepoFilter} />
  ) : undefined

  return (
    <TaskListView
      tasks={tasks}
      isError={isError}
      isLoading={isLoading}
      isFetching={isFetching}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      hasNextPage={hasNextPage}
      openCount={counts?.open}
      closedCount={counts?.closed}
      emptyHeading="No agent sessions"
      emptyDescription="Start a new session to see it here."
      additionalFilters={additionalFilters}
      renderItem={task => (
        <TaskListItem
          key={task.id ?? task.clientGeneratedId}
          task={task}
          nwo={task.nwo}
          currentUserId={currentUserId}
          dataUpdatedAt={dataUpdatedAt}
          analyticsTarget="AGENTS_PAGE_TASK"
          renderDescription={(t, props) => <TaskDescription {...props} parts={buildDescriptionParts(t)} />}
        />
      )}
    />
  )
}

function buildDescriptionParts(task: UITask): ReactNode[] {
  if (task.isOptimistic) {
    return [task.nwo ? <span key="repo">{task.nwo}</span> : null, <span key="init">Initializing session...</span>]
  }

  const pullText = task.pull?.number ? `#${task.pull.number}` : null
  const repoAndPull = [task.nwo, pullText].filter(Boolean).join(' ')

  return [
    repoAndPull ? <span key="repo">{repoAndPull}</span> : null,
    <span key="time">
      <TaskRelativeTime task={task} />
    </span>,
    task.session_count > 1 ? <span key="revisions">{task.session_count} revisions</span> : null,
    <span key="state">{getSessionStateText(task)}</span>,
  ]
}
